import React, { useCallback, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import TextInput from "../../../Components/Fields/TextInput.js";
import api from "../../../conf/api.js";
import TimeDurationPicker from "../../../Components/Fields/TimeDurationPicker.js";
import dayjs from "dayjs";

/**
 * Feed Scheduler Dialog
 * Configure publishing schedule for MRSS feeds
 */
export default function FeedSchedulerBox({ open, selectedFeed, onClose }) {
  let initialFormData = {
    feed: null,
    selected_days: [],
    time_from: "",
    time_to: "",
    uploading_gap: "",
  };

  let daysOfWeek = [
    { value: 0, label: "Monday" },
    { value: 1, label: "Tuesday" },
    { value: 2, label: "Wednesday" },
    { value: 3, label: "Thursday" },
    { value: 4, label: "Friday" },
    { value: 5, label: "Saturday" },
    { value: 6, label: "Sunday" },
  ];

  const [isUpdate, setIsUpdate] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [feeds, setFeeds] = useState([]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleDays = (event) => {
    const value = parseInt(event.target.value);
    if (
      formData.selected_days?.find((day) => day === value) ||
      formData.selected_days?.find((day) => day === value) === 0
    ) {
      setFormData((pre) => ({
        ...pre,
        selected_days: pre.selected_days.filter((d) => d !== value),
      }));
    } else {
      setFormData((pre) => ({
        ...pre,
        selected_days: [...pre.selected_days, value],
      }));
    }
  };

  const handleDuration = (field_name) => (_value) => {
    setFormData((pre) => ({ ...pre, [field_name]: _value }));
  };

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = formatData();
    if (!isValidTimeRange(data.time_from, data.time_to)) {
      toast.error("Invalid time range. 'From' time must be before 'To' time.");
      return;
    }

    if (data.uploading_gap < 1) {
      toast.error("Min gap must be grater than 0.");
      return;
    }

    if (isUpdate) {
      await updateFeedScheduler(data);
    } else {
      await addFeedScheduler(data);
    }
  };

  const updateFeedScheduler = async (data) => {
    try {
      const response = await api.patch(
        `/mrss/feed-schedulers/${formData.id}/`,
        data
      );
      if (response.status === 200) {
        toast.success("Feed Scheduler updated successfully.");
        onClose();
      }
    } catch (error) {
      handleErrorResponse(error);
    }
  };

  const addFeedScheduler = async (data) => {
    try {
      const response = await api.post("/mrss/feed-schedulers/", data);
      if (response.status === 201) {
        toast.success("Feed Scheduler added successfully.");
        onClose();
      }
    } catch (error) {
      handleErrorResponse(error);
    }
  };

  const handleErrorResponse = (error) => {
    const errorObject = error.response?.data;
    if (errorObject) {
      for (const field in errorObject) {
        if (errorObject.hasOwnProperty(field)) {
          const errors = Array.isArray(errorObject[field])
            ? errorObject[field]
            : [errorObject[field]];
          errors.forEach((errorMessage) => {
            toast.error(`${field}: ${errorMessage}`);
          });
        }
      }
    } else {
      toast.error("Something went wrong");
    }
  };

  async function fetchSchedulerData() {
    try {
      const response = await api.get(`/mrss/feed-scheduler/${selectedFeed.id}/`);
      if (response.status === 200 && response.data) {
        var _data = response.data;
        _data.time_from = convertStringToDuration(_data.time_from);
        _data.time_to = convertStringToDuration(_data.time_to);
        setIsUpdate(true);
        setFormData(_data);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Error fetching scheduler:", error);
      }
      // If 404, it means no scheduler exists yet (create mode)
    }
  }

  async function fetchFeeds() {
    try {
      const response = await api.get("/mrss/feeds/", {
        params: { page: 1, page_size: 10000 },
      });
      const feedsList = response.data?.results || response.data;
      setFeeds(feedsList);

      if (!isUpdate && selectedFeed) {
        const feed = feedsList.find((f) => f.id === selectedFeed.id);
        if (feed) {
          setFormData((pre) => ({ ...pre, feed: feed }));
        }
      }
    } catch (error) {
      console.error("Error fetching feeds:", error);
    }
  }

  const convertStringToDuration = useCallback((_time) => {
    if (!_time) return null;
    const [hour, min, sec] = _time.split(":");
    return dayjs().hour(parseInt(hour)).minute(parseInt(min)).second(parseInt(sec || 0)).millisecond(0);
  }, []);

  const convertDurationToUTCString = useCallback((dayjsTime) => {
    if (!dayjsTime) return "";
    if (typeof dayjsTime === "string") return dayjsTime;
    if (!dayjs.isDayjs(dayjsTime)) return "";
    const pad = (n) => n.toString().padStart(2, "0");
    return `${pad(dayjsTime.hour())}:${pad(dayjsTime.minute())}:${pad(dayjsTime.second())}`;
  }, []);

  const SelectDays = useCallback(
    () =>
      daysOfWeek.map((day) => (
        <Grid item key={day.value}>
          <FormControlLabel
            control={
              <Checkbox
                value={day.value}
                onChange={handleDays}
                checked={
                  formData.selected_days?.find((d) => d === day.value) ||
                  formData.selected_days?.find((d) => d === day.value) === 0
                }
              />
            }
            label={day.label}
          />
        </Grid>
      )),
    // eslint-disable-next-line
    [formData.selected_days]
  );

  useEffect(() => {
    if (open && selectedFeed) {
      fetchSchedulerData();
      fetchFeeds();
    }
    // eslint-disable-next-line
  }, [open, selectedFeed]);

  function formatData() {
    let _data = { ...formData };
    _data.feed = formData.feed?.id;
    _data.time_from = convertDurationToUTCString(_data.time_from);
    _data.time_to = convertDurationToUTCString(_data.time_to);
    return _data;
  }

  const isValidTimeRange = (timeFrom, timeTo) => {
    const [fromHour, fromMin, fromSec] = timeFrom.split(":").map(Number);
    const [toHour, toMin, toSec] = timeTo.split(":").map(Number);
    const fromTotalSeconds = fromHour * 3600 + fromMin * 60 + fromSec;
    const toTotalSeconds = toHour * 3600 + toMin * 60 + toSec;
    return fromTotalSeconds < toTotalSeconds;
  };

  return (
    <Dialog maxWidth="md" open={open} onClose={handleClose}>
      <DialogTitle>
        {isUpdate ? "Update" : "Add"} Feed Scheduler
      </DialogTitle>
      <DialogContent>
        <DialogContentText mb={2}>
          Configure the publishing schedule for this MRSS feed. Videos will be
          published automatically according to these rules.
        </DialogContentText>
        <form onSubmit={handleSubmit}>
          <Grid container marginBottom={3} spacing={3} alignItems="center">
            <Grid item xs={12}>
              <Autocomplete
                readOnly={isUpdate}
                value={formData.feed}
                options={feeds}
                getOptionLabel={(op) => op.name || ""}
                size="small"
                fullWidth
                placeholder="Select Feed"
                onChange={(event, newValue) => {
                  setFormData((pre) => ({ ...pre, feed: newValue }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Feed"
                    helperText="Select the MRSS feed to configure"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Publishing Days
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Select the days when videos can be published
              </Typography>
            </Grid>
            <Grid
              container
              item
              sx={{ justifyContent: "space-between", padding: "0 0 0 16px" }}
            >
              <SelectDays />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Publishing Time Window
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Videos will only be published within this time range
              </Typography>
            </Grid>
            <Grid item container xs={12} spacing={2}>
              <Grid item xs={6}>
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <TimeDurationPicker
                    sx={{ width: "100%" }}
                    required
                    label="From (UTC)"
                    value={formData.time_from}
                    onChange={handleDuration("time_from")}
                  />
                  <Typography variant="caption">
                    Enter time in UTC.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <TimeDurationPicker
                    sx={{ width: "100%" }}
                    ampm={false}
                    required
                    label="To (UTC)"
                    value={formData.time_to}
                    onChange={handleDuration("time_to")}
                  />
                  <Typography variant="caption">
                    Enter time in UTC.
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Publishing Gap
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Time between video publications (in minutes)
              </Typography>
            </Grid>
            <Grid item>
              <TextInput
                label="Publishing Gap (minutes)"
                type="number"
                fullWidth
                required
                name="uploading_gap"
                value={formData.uploading_gap}
                onChange={handleChange}
                helperText="Gap time between publications"
              />
            </Grid>

          </Grid>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" type="submit" color="primary">
              {isUpdate ? "Update" : "Add"} Feed Scheduler
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}
